/**
 * Connectivity Validator
 * Validates that all required handles are connected
 */

import { Edge } from '@xyflow/react'
import { WorkflowNode } from '@store/workflowStore'
import { WorkflowNodeType } from '@features/adventures/types/workflow.types'
import {
  ValidationIssue,
  ValidationSeverity,
  ValidationCategory,
  ValidationErrorCodes,
} from '@features/adventures/types/validation.types'
import {
  createIssue,
  groupEdgesBySource,
  groupEdgesByTarget,
  hasOutgoingEdge,
  hasIncomingEdge,
  hasOutgoingEdgeFromHandle,
  isExitNodeType,
} from './helpers'

/**
 * Get required source handles for a node based on its type and configuration
 */
function getRequiredSourceHandles(node: WorkflowNode): string[] {
  const { nodeType, numpadConfig, qrCodeConfig, questionAnswerConfig } = node.data

  switch (nodeType) {
    case WorkflowNodeType.Loop:
      return ['continue', 'maxReached']

    case WorkflowNodeType.Delay:
      return ['flow-source']

    case WorkflowNodeType.QuestionAnswer: {
      const qaHandles = ['correct']
      if (questionAnswerConfig?.allowSkip) {
        qaHandles.push('cancel', 'skip')
      }
      return qaHandles
    }

    case WorkflowNodeType.QRCode: {
      const qrHandles = ['cancel', 'failed']
      const sharedCodes = qrCodeConfig?.sharedCodes || []
      sharedCodes.forEach((_, i) => qrHandles.push(`code-${i}`))
      return qrHandles
    }

    case WorkflowNodeType.NumericInput: {
      const numHandles = ['failed']
      const acceptedNumbers = numpadConfig?.acceptedNumbers || []
      acceptedNumbers.forEach((_, i) => numHandles.push(`code-${i}`))
      return numHandles
    }

    case WorkflowNodeType.CodexInput: {
      const codexHandles = ['failed']
      const acceptedCodes = node.data.codexConfig?.acceptedCodes || []
      acceptedCodes.forEach((_, i) => codexHandles.push(`code-${i}`))
      return codexHandles
    }

    case WorkflowNodeType.ModeratorApproval:
      return ['approved', 'denied', 'timeout']

    case WorkflowNodeType.Decision: {
      // Decision nodes need flow-source for default path
      // Branch handles are based on conditions
      const handles = ['flow-source']
      const conditions = node.data.conditions || []
      conditions.forEach(c => {
        if (c.branchKey) {
          handles.push(c.branchKey)
        }
      })
      return handles
    }

    // Exit nodes don't need outgoing connections
    case WorkflowNodeType.Exit:
    case WorkflowNodeType.ExitSuccess:
    case WorkflowNodeType.ExitFailed:
    case WorkflowNodeType.ExitAbandoned:
      return []

    // Most reward and other nodes just need flow-source
    default:
      return ['flow-source']
  }
}

/**
 * Get human-readable handle name for error messages
 */
function getHandleDisplayName(handleId: string, nodeType: WorkflowNodeType): string {
  // Common handles
  switch (handleId) {
    case 'flow-source':
      return 'output'
    case 'flow-target':
      return 'input'
    case 'cancel':
      return 'Cancel'
    case 'skip':
      return 'Skip'
    case 'correct':
      return 'Correct'
    case 'failed':
      return 'Failed'
    case 'continue':
      return 'Continue'
    case 'maxReached':
      return 'Max Reached'
    case 'timeout':
      return 'Timeout'
    case 'approved':
      return 'Approved'
    case 'denied':
      return 'Denied'
  }

  // Code handles (code-0, code-1, etc.)
  if (handleId.startsWith('code-')) {
    const index = parseInt(handleId.replace('code-', ''), 10)
    return `Code ${index + 1}`
  }

  return handleId
}

/**
 * Validates that all required handles are connected
 */
export function validateConnectivity(nodes: WorkflowNode[], edges: Edge[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Check for self-connections (edges where source === target)
  for (const edge of edges) {
    if (edge.source === edge.target) {
      const node = nodes.find(n => n.id === edge.source)
      issues.push(createIssue({
        nodeId: edge.source,
        nodeLabel: node?.data.label,
        nodeType: node?.data.nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Connectivity,
        code: ValidationErrorCodes.SELF_CONNECTION,
        message: 'Node cannot connect to itself',
      }))
    }
  }

  // Build edge lookup maps
  const edgesBySource = groupEdgesBySource(edges)
  const edgesByTarget = groupEdgesByTarget(edges)

  for (const node of nodes) {
    const nodeType = node.data.nodeType

    // Start nodes don't need incoming connections
    if (nodeType === WorkflowNodeType.Start) {
      // Validate Start has at least one outgoing connection
      if (!hasOutgoingEdge(node.id, edgesBySource)) {
        issues.push(createIssue({
          nodeId: node.id,
          nodeLabel: node.data.label,
          nodeType,
          severity: ValidationSeverity.Error,
          category: ValidationCategory.Connectivity,
          code: ValidationErrorCodes.MISSING_SOURCE_CONNECTION,
          message: 'Start node must have an outgoing connection',
        }))
      }
      continue
    }

    // Exit nodes don't need outgoing connections
    if (isExitNodeType(nodeType)) {
      // Validate Exit has at least one incoming connection (warning only)
      if (!hasIncomingEdge(node.id, edgesByTarget)) {
        issues.push(createIssue({
          nodeId: node.id,
          nodeLabel: node.data.label,
          nodeType,
          severity: ValidationSeverity.Warning,
          category: ValidationCategory.Connectivity,
          code: ValidationErrorCodes.MISSING_TARGET_CONNECTION,
          message: 'Exit node has no incoming connections (unreachable)',
        }))
      }
      continue
    }

    // All other nodes need incoming connection to flow-target
    if (!hasIncomingEdge(node.id, edgesByTarget, 'flow-target')) {
      issues.push(createIssue({
        nodeId: node.id,
        nodeLabel: node.data.label,
        nodeType,
        severity: ValidationSeverity.Error,
        category: ValidationCategory.Connectivity,
        code: ValidationErrorCodes.MISSING_TARGET_CONNECTION,
        message: 'Node has no incoming connection',
        handleId: 'flow-target',
      }))
    }

    // Validate required source handles are connected
    const requiredHandles = getRequiredSourceHandles(node)
    for (const handleId of requiredHandles) {
      if (!hasOutgoingEdgeFromHandle(node.id, handleId, edgesBySource)) {
        const handleName = getHandleDisplayName(handleId, nodeType)
        issues.push(createIssue({
          nodeId: node.id,
          nodeLabel: node.data.label,
          nodeType,
          severity: ValidationSeverity.Error,
          category: ValidationCategory.Connectivity,
          code: ValidationErrorCodes.MISSING_HANDLE_CONNECTION,
          message: `"${handleName}" handle is not connected`,
          handleId,
        }))
      }
    }
  }

  return issues
}
